import { useDispatch, useSelector } from "react-redux";
import { useState, useCallback, useEffect } from "react";
import { Dialog } from "@mui/material";
import { useDialog } from "app/hook";
import { DialogType } from "app/types";
import { AppDispatch, RootState } from "app/store";
import { actions as configActions } from "app/configSlice";
import { useScale } from "common/utils";
import { FadeTransition, ParentCloseBtn } from "common/elements";
import parentI18n from "features/parent/i18n";
import { QrLoginForm } from "./QrLoginForm";
import { EmailLoginForm } from "./EmailLoginForm";
import { EmailRegisterForm } from "./EmailRegisterForm";
import { PasswordResetForm } from "./PasswordResetForm";
import { AuthMethodSelector, AuthMode } from "./AuthMethodSelector";
import { RegisterMethod, setRegisterSource, recordRegisterStart } from "./registerAnalytics";

enum ParentAccountAuthStep {
  Email = "email",
  PasswordReset = "passwordReset",
  Selection = "selection",
  QrLogin = "qrLogin",
}

export const ParentAccountAuthDialog = () => {
  const dispatch = useDispatch<AppDispatch>();
  const handleDialog = useDialog();
  const { scale } = useScale();
  const dialogState = useSelector((state: RootState) => state.config.dialog.parent_registration);
  const visible = dialogState.value;
  // 開いたところ（GA の REGISTER_* の source）
  const openedFrom = dialogState.args?.source;
  const [mode, setMode] = useState<AuthMode>(AuthMode.Signup);
  const [step, setStep] = useState<ParentAccountAuthStep>(ParentAccountAuthStep.Selection);

  const close = useCallback(() => {
    handleDialog({ type: DialogType.PARENT_REGISTRATION, value: false });
  }, [handleDialog]);

  const resetDialog = useCallback(() => {
    setMode(AuthMode.Signup);
    setStep(ParentAccountAuthStep.Selection);
  }, []);

  useEffect(() => {
    if (visible) {
      dispatch(configActions.setLastClaimStatus(null));
      setRegisterSource(openedFrom ?? "unknown");
    } else {
      setRegisterSource(null);
      // 認証成功時の close は AuthProvider が担うため、
      // 非表示化を検知して次回オープン用に local 状態を戻す
      resetDialog();
      // 認証成功時はフォーム/SSO がローディングを維持したままダイアログを閉じる
      // ため、閉鎖を検知して最前面ローディングを確実にクリアする
      handleDialog({ type: DialogType.LOADING, value: false });
    }
  }, [visible, openedFrom, dispatch, resetDialog, handleDialog]);

  const handleClose = () => {
    resetDialog();
    close();
  };

  const handleGoEmail = () => {
    setStep(ParentAccountAuthStep.Email);
  };

  const handleModeSwitch = (nextMode: AuthMode) => {
    setMode(nextMode);
    setStep(ParentAccountAuthStep.Selection);
  };

  const handleBackToSelection = () => {
    setStep(ParentAccountAuthStep.Selection);
  };

  const handleForgotPassword = () => {
    setStep(ParentAccountAuthStep.PasswordReset);
  };

  const handleQrLogin = () => {
    recordRegisterStart(RegisterMethod.QR);
    setStep(ParentAccountAuthStep.QrLogin);
  };

  const handleBackToLoginFromReset = () => {
    setMode(AuthMode.Login);
    setStep(ParentAccountAuthStep.Email);
  };

  const handleAuthLoadingChange = useCallback(
    (loading: boolean) => {
      handleDialog({ type: DialogType.LOADING, value: loading });
    },
    [handleDialog],
  );

  // SSO 成功時のクローズ・遷移・claim 判定も AuthProvider が一元的に担うため、
  // ここでは navigate("/") を抑止する目的の noop を渡す（処理は AuthProvider に委譲）。
  const handleSsoSuccess = useCallback(() => {
    // 何もしない（AuthProvider の onAuthStateChanged callback に委譲）
  }, []);

  // 認証成功後の Modal クローズ・遷移・claim 判定は AuthProvider が一元的に担う
  // （parentsAuth onAuthStateChanged → handleFetchParentsUser callback）。
  // 本コンポーネントは認証 UI の表示のみを責務とする。

  const form =
    mode === AuthMode.Signup ? (
      <EmailRegisterForm
        onLoadingChange={handleAuthLoadingChange}
        showSuccessToast
        keepLoadingOnSuccess
      />
    ) : (
      <EmailLoginForm
        onForgotPassword={handleForgotPassword}
        onLoadingChange={handleAuthLoadingChange}
        showSuccessToast
        keepLoadingOnSuccess
      />
    );

  return (
    <Dialog
      fullScreen
      open={visible}
      maxWidth={false}
      sx={{ zIndex: 9999999999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el w-[520px] border-solid border-gray2-30 border-[16px] rounded-2xl bg-gray2-30">
            <div className="flex-col-el justify-start w-full relative p-6 rounded-lg bg-white gap-4">
              <ParentCloseBtn close={handleClose} />

              <div className="flex-row-el items-center gap-2 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined type-hero text-blue-100">
                  family_restroom
                </span>
                <p className="type-text type-title text-textcolor-black leading-tight">
                  {parentI18n.t("MSG_PARENT_REGISTRATION_TITLE")}
                </p>
              </div>

              <div className="flex-col-el items-center justify-center w-full h-[560px] gap-6 py-2">
                {step === ParentAccountAuthStep.PasswordReset ? (
                  <div className="flex-col-el items-center w-[375px] gap-4">
                    <PasswordResetForm
                      onBackToLogin={handleBackToLoginFromReset}
                      onLoadingChange={handleAuthLoadingChange}
                      showBackBeforeSent
                    />
                  </div>
                ) : step === ParentAccountAuthStep.Selection ? (
                  <>
                    <AuthMethodSelector
                      mode={mode}
                      onEmailSelect={handleGoEmail}
                      onModeSwitch={handleModeSwitch}
                      onSsoSuccess={handleSsoSuccess}
                      keepLoadingOnSuccess
                    />

                    <div className="flex-row-el items-center w-[375px] gap-3">
                      <div className="flex-1 h-px bg-gray2-60" />
                      <span className="type-text type-sm text-gray2-100">
                        {parentI18n.t("MSG_AUTH_OR_DIVIDER")}
                      </span>
                      <div className="flex-1 h-px bg-gray2-60" />
                    </div>

                    <div className="flex-col-el items-center w-[375px] gap-2">
                      <button
                        type="button"
                        onClick={handleQrLogin}
                        className="flex-row-el items-center justify-center gap-2 w-full py-3 bg-white box-border border-solid border border-gray2-60 text-textcolor-black rounded-xl cursor-pointer hover:bg-gray2-20 transition-all type-inherit-font type-text type-body"
                      >
                        <span className="material-symbols-outlined">qr_code_scanner</span>
                        {parentI18n.t("MSG_QR_LOGIN_SCAN_BTN")}
                      </button>
                      <p className="type-text type-sm text-gray2-100 text-center">
                        {parentI18n.t("MSG_QR_LOGIN_SELECT_HINT")}
                      </p>
                    </div>
                  </>
                ) : step === ParentAccountAuthStep.QrLogin ? (
                  <div className="flex-col-el items-center w-full gap-4">
                    <QrLoginForm />
                    <button
                      type="button"
                      onClick={handleBackToSelection}
                      className="bg-transparent border-none text-textcolor-black/70 hover:text-textcolor-black cursor-pointer type-inherit-font type-text type-sm flex-row-el items-center gap-1"
                    >
                      <span className="material-symbols-outlined type-md">arrow_back</span>
                      {parentI18n.t("MSG_BACK_BTN")}
                    </button>
                  </div>
                ) : (
                  <div className="flex-col-el items-center w-[375px] gap-4">
                    {form}
                    <button
                      type="button"
                      onClick={handleBackToSelection}
                      className="bg-transparent border-none text-textcolor-black/70 hover:text-textcolor-black cursor-pointer type-inherit-font type-text type-sm flex-row-el items-center gap-1"
                    >
                      <span className="material-symbols-outlined type-md">arrow_back</span>
                      {parentI18n.t("MSG_BACK_BTN")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
